import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { AvailabilitySlotsDocument, AvailabilitySlotsQueryResult } from '@/types-aggregatore';
import { getStartOfDayTime } from '@/app/_components/widgets/listing/_utils/time';
import client from '@/utils/apolloClient';

interface UseAvailabilitySlotsProps {
  specialityId: string;
  workgroupId: string;
  enabled?: boolean;
  firstAvailabilityDate?: number | null;
  selectedDoctorId?: string;
}

type AvailabilitySlots = AvailabilitySlotsQueryResult['availabilitySlots'];

export const useAvailabilitySlots = ({
  specialityId,
  workgroupId,
  enabled = true,
  firstAvailabilityDate,
  selectedDoctorId,
}: UseAvailabilitySlotsProps) => {
  const initialized = useRef(false);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allData, setAllData] = useState<AvailabilitySlots>([]);
  const [hasMoreData, setHasMoreData] = useState(true);

  const fetchData = useCallback(
    async (timestamp: number | null, initialFetch = false) => {
      if (isLoadingMore || !enabled) return;
      setIsLoadingMore(true);

      try {
        let availabilitySlots = [];
        let hasMore = false;

        const { data } = await client.query({
          query: AvailabilitySlotsDocument,
          variables: {
            ...(timestamp && { startDate: timestamp }),
            specialityId,
            doctorId: selectedDoctorId,
            workgroupId,
          },
        });

        availabilitySlots = data.availabilitySlots;
        hasMore = availabilitySlots.length > 0;

        if (initialFetch && availabilitySlots.length === 0 && firstAvailabilityDate) {
          const { data: dataFromFirstAvailableDate } = await client.query({
            query: AvailabilitySlotsDocument,
            variables: {
              startDate: firstAvailabilityDate,
              specialityId,
              doctorId: selectedDoctorId,
              workgroupId,
            },
          });

          availabilitySlots = dataFromFirstAvailableDate.availabilitySlots;
          hasMore = dataFromFirstAvailableDate.availabilitySlots.length > 0;
        }

        setHasMoreData(hasMore);

        setAllData(prev => {
          return initialFetch ? [...availabilitySlots] : [...prev, ...availabilitySlots];
        });
      } catch (error) {
        console.error('Error fetching availability slots:', error);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [isLoadingMore, specialityId, workgroupId, enabled, firstAvailabilityDate, selectedDoctorId],
  );

  useEffect(() => {
    if (!enabled) return;

    const fetchInitialData = async () => {
      try {
        await fetchData(null, true);
      } catch (error) {
        console.error('Error fetching availability slots:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    setIsInitialLoading(true);
    setAllData([]);
    fetchInitialData();
  }, [specialityId, selectedDoctorId, workgroupId, fetchData, enabled]);

  const fetchNextPage = async () => {
    if (allData.length === 0) return;
    // Get the last slot's date and add 1 day
    const lastSlotDate = new Date(allData[allData.length - 1].start);
    lastSlotDate.setDate(lastSlotDate.getDate() + 1);
    // Fetch from the next day
    return fetchData(lastSlotDate.getTime());
  };

  const groupedDays = useMemo(() => {
    if (!allData.length) return {};

    const nowAsMilliseconds = new Date().getTime();
    const daysByDate: Record<string, Record<number, NonNullable<AvailabilitySlots>[number]>> = {};

    for (const slot of allData) {
      /**
       *  ? For unknown reasons, availability slots query, sometimes returns slots with a start date in the past
       *
       *  For a query at 15:52 it returned a slot with a start date of 11:40, on 11 september 2025
       */
      if (!slot || slot.start < nowAsMilliseconds) continue;

      const dayKey = getStartOfDayTime(slot.start);
      const startKey = slot.start;

      const slotsByStart = (daysByDate[dayKey] ||= {});
      const existing = slotsByStart[startKey];

      if (!existing || (slot.price ?? 0) < (existing.price ?? 0)) {
        slotsByStart[startKey] = slot;
      }
    }

    return Object.entries(daysByDate).reduce<Record<string, NonNullable<AvailabilitySlots>>>((acc, [dayKey, slotsByStart]) => {
      acc[dayKey] = Object.values(slotsByStart);
      return acc;
    }, {});
  }, [allData]);

  const sortedDates = useMemo(() => Object.keys(groupedDays).map(Number).sort(), [groupedDays]);

  return {
    groupedDays,
    sortedDates,
    isInitialLoading,
    isLoadingMore,
    fetchNextPage,
    hasMoreData,
  };
};
